import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { CancelledIcon, ExpandIcon } from 'images'
import { computeAspectRatio, fileStatus } from 'components/Recommendations/Campaign/Media/media'
import { useTranslation } from 'react-i18next'
import CustomCheckBox from 'components/Shared/CustomCheckBox'
import { DotsMenuIcon } from 'images'
import ActionMenu from 'components/Shared/MediaCard/ActionMenu'
import ProgressBar from 'components/Shared/MediaCard/ProgressBar'
import MediaLabel from 'components/Shared/MediaCard/MediaLabel'

const MediaPreview = ({ file }) => {
  const src = file.preview || file.url

  if (file.type?.startsWith('video')) {
    return <video className="preview-media-card__preview-media" controls={false} src={src} title={file.name || file.filename} />
  }

  return <img className="preview-media-card__preview-media" src={src} alt={file.name || file.filename} />
}

export default function PreviewMediaCard(props) {
  const {
    item,
    selectiveMode = false,
    selectedMedia = [],
    setSelectedMedia = () => {},
    onExpandClick,
    actionMenuItems = [],
    onClick,
    onRemove,
    mediaLabel,
    slotRatio,
  } = props

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuContainerRef = useRef(null)

  const isError = item.status === fileStatus.uploadFailed
  const isUploading = item.status === fileStatus.new || item.status === fileStatus.uploading
  const isSelected = selectedMedia.includes(item.id)
  // 9:16 is always shown as the slot ratio itself, never recomputed from the
  // image — it's Meta's Reels placement (and, for Google Display, exists
  // only to cover that placement), so there's no finer "real" ratio worth
  // surfacing there. slotRatio is only known when this card is rendered for
  // a specific required slot (e.g. the media group preview modal); callers
  // without that context (e.g. the co-create results grid) fall back to the
  // existing computed/stored ratio.
  const displayRatio = slotRatio === '9:16'
    ? slotRatio
    : (item.width && item.height
      ? computeAspectRatio(item.width, item.height)
      : null) ?? item.ratio
  const { t } = useTranslation()

  const handleToggleSelection = () => {
    setSelectedMedia((prev) =>
      prev.includes(item.id)
        ? prev.filter((id) => id !== item.id)
        : [...prev, item.id],
    )
  }

  const handleExpandClick = (event) => {
    event.stopPropagation()
    onExpandClick?.(item)
  }

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (menuContainerRef.current?.contains(event.target)) {
        return
      }

      setIsMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  return (
    <div
      className={classnames('preview-media-card', {
        'selected': isSelected,
        'selective-mode-item': selectiveMode,
        'preview-media-card--interactive': Boolean(onClick),
      })}
      onClick={onClick ? () => onClick(item) : undefined}
    >
      <div
        className={classnames('preview-media-card__preview', {
        })}
      >
        <MediaPreview file={item} />

        {isError && <div className="error-overlay">
          <div className="icon">
            <CancelledIcon width={12} height={12} />
          </div>
          <p className="title">{t('common.genericError')}</p>

          {onRemove && <p className="error-message" onClick={(event) => {
            event.stopPropagation()
            onRemove(item)
          }}>{t('Recommendations.campaign.setup.media.removeMedia')}</p>}
        </div>}

        {isUploading && <div className="uploading-overlay">
          <ProgressBar progress={item.progress ?? 0} />
        </div>}

        {onExpandClick && <button type="button" className="expand-button" onClick={handleExpandClick}>
          <ExpandIcon width={16} height={16} />
        </button>}

        {!selectiveMode && !isError && !isUploading && actionMenuItems.length > 0 && (
          <div className="media-list-card__actions" ref={menuContainerRef}>
            <button
              className="dots-menu-btn"
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setIsMenuOpen((open) => !open)
              }}
            >
              <DotsMenuIcon />
            </button>

            {isMenuOpen && (
              <div className="media-action-menu-popper">
                <ActionMenu
                  actionMenuItems={actionMenuItems}
                  setIsMenuOpen={setIsMenuOpen}
                />
              </div>
            )}
          </div>
        )}

        {!isUploading && !isError && <div className='info-container'>
          {mediaLabel && !isError && !isUploading && (
            <MediaLabel variant={mediaLabel.variant} bothSelected={mediaLabel.bothSelected} position="static" />
          )}

          <div className='info-item-container mt-2'>
            <p className='info-item'>{displayRatio}</p>
            <p className='info-item'>{item.width} x {item.height}</p>
          </div>
        </div>}
      </div>

      {selectiveMode && (
        <div
          className={classnames('selective-mode-overlay', {
            'selective-mode-overlay--selected': isSelected,
          })}
          onClick={handleToggleSelection}
        >
          {selectiveMode && (
            <div
              className="selective-mode-overlay__checkbox"
              onClick={(event) => event.stopPropagation()}
            >
              <CustomCheckBox
                checked={isSelected}
                onChange={handleToggleSelection}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

PreviewMediaCard.propTypes = {
  item: PropTypes.object.isRequired,
  selectiveMode: PropTypes.bool,
  selectedMedia: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedMedia: PropTypes.func,
  onExpandClick: PropTypes.func,
  onRemove: PropTypes.func,
  onClick: PropTypes.func,
  mediaLabel: PropTypes.shape({
    variant: PropTypes.string,
    bothSelected: PropTypes.bool,
  }),
  slotRatio: PropTypes.string,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
}
